// Standard fragrance bottle size conversions
// Based on common industry sizes and user specifications

interface SizeConversion {
  oz: number;
  ml: number;
}

// Standard fragrance size mappings
const STANDARD_SIZES: SizeConversion[] = [
  { oz: 0.03, ml: 1 },
  { oz: 0.05, ml: 1.5 },
  { oz: 0.07, ml: 2 },
  { oz: 0.17, ml: 5 },
  { oz: 0.34, ml: 10 },
  { oz: 0.5, ml: 15 },
  { oz: 1, ml: 30 },
  { oz: 1.7, ml: 50 },
  { oz: 2, ml: 60 },
  { oz: 2.5, ml: 75 },
  { oz: 3, ml: 90 },
  { oz: 3.4, ml: 100 },
  { oz: 4.2, ml: 125 },
  { oz: 5, ml: 150 },
  { oz: 6.7, ml: 200 }
];

/**
 * Convert oz to ml using standard fragrance sizes
 * @param oz - Size in oz
 * @returns Size in ml (rounded to 1 decimal place)
 */
export const ozToMl = (oz: number): number => {
  if (!oz || oz <= 0) return 0;
  
  // Check for exact matches first
  const exactMatch = STANDARD_SIZES.find(size => Math.abs(size.oz - oz) < 0.01);
  if (exactMatch) {
    return exactMatch.ml;
  }
  
  // Find closest size for interpolation
  const sortedSizes = STANDARD_SIZES.sort((a, b) => a.oz - b.oz);
  
  // If below minimum, use ratio of smallest size
  if (oz < sortedSizes[0].oz) {
    const ratio = sortedSizes[0].ml / sortedSizes[0].oz;
    return Math.round(oz * ratio * 10) / 10;
  }
  
  // If above maximum, use ratio of largest size
  if (oz > sortedSizes[sortedSizes.length - 1].oz) {
    const ratio = sortedSizes[sortedSizes.length - 1].ml / sortedSizes[sortedSizes.length - 1].oz;
    return Math.round(oz * ratio * 10) / 10;
  }
  
  // Interpolate between two closest sizes
  for (let i = 0; i < sortedSizes.length - 1; i++) {
    const lower = sortedSizes[i];
    const upper = sortedSizes[i + 1];
    
    if (oz >= lower.oz && oz <= upper.oz) {
      const ratio = (oz - lower.oz) / (upper.oz - lower.oz);
      const ml = lower.ml + ratio * (upper.ml - lower.ml);
      return Math.round(ml * 10) / 10;
    }
  }
  
  // Fallback to standard conversion (shouldn't reach here)
  return Math.round(oz * 29.5735 * 10) / 10;
};

/**
 * Convert ml to oz using standard fragrance sizes
 * @param ml - Size in ml
 * @returns Size in oz (rounded to 1 decimal place)
 */
export const mlToOz = (ml: number): number => {
  if (!ml || ml <= 0) return 0;
  
  // Check for exact matches first
  const exactMatch = STANDARD_SIZES.find(size => Math.abs(size.ml - ml) < 0.1);
  if (exactMatch) {
    return exactMatch.oz;
  }
  
  // Find closest size for interpolation
  const sortedSizes = STANDARD_SIZES.sort((a, b) => a.ml - b.ml);
  
  // If below minimum, use ratio of smallest size
  if (ml < sortedSizes[0].ml) {
    const ratio = sortedSizes[0].oz / sortedSizes[0].ml;
    return Math.round(ml * ratio * 10) / 10;
  }
  
  // If above maximum, use ratio of largest size
  if (ml > sortedSizes[sortedSizes.length - 1].ml) {
    const ratio = sortedSizes[sortedSizes.length - 1].oz / sortedSizes[sortedSizes.length - 1].ml;
    return Math.round(ml * ratio * 10) / 10;
  }
  
  // Interpolate between two closest sizes
  for (let i = 0; i < sortedSizes.length - 1; i++) {
    const lower = sortedSizes[i];
    const upper = sortedSizes[i + 1];
    
    if (ml >= lower.ml && ml <= upper.ml) {
      const ratio = (ml - lower.ml) / (upper.ml - lower.ml);
      const oz = lower.oz + ratio * (upper.oz - lower.oz);
      return Math.round(oz * 10) / 10;
    }
  }
  
  // Fallback to standard conversion (shouldn't reach here)
  return Math.round(ml * 0.033814 * 10) / 10;
};

/**
 * Get the closest standard size for a given oz value
 * @param oz - Size in oz
 * @returns Closest standard size object
 */
export const getClosestStandardSize = (oz: number): SizeConversion | null => {
  if (!oz || oz <= 0) return null;
  
  let closest = STANDARD_SIZES[0];
  let minDiff = Math.abs(oz - closest.oz);
  
  for (const size of STANDARD_SIZES) {
    const diff = Math.abs(oz - size.oz);
    if (diff < minDiff) {
      minDiff = diff;
      closest = size;
    }
  }
  
  return closest;
};

/**
 * Check if a size is a standard fragrance size
 * @param oz - Size in oz
 * @param ml - Size in ml
 * @returns True if it matches a standard size
 */
export const isStandardSize = (oz?: number, ml?: number): boolean => {
  if (oz) {
    return STANDARD_SIZES.some(size => Math.abs(size.oz - oz) < 0.01);
  }
  if (ml) {
    return STANDARD_SIZES.some(size => Math.abs(size.ml - ml) < 0.1);
  }
  return false;
};
